import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";

import {
  getFundingCampaignSubmission,
  updateFundingCampaignStatus,
  FundingCampaignStatus,
  FundingCampaignSubmissionDetail,
  FundingCampaignHistoryEntry,
  FundingCampaignNote,
} from "../../../services/fundingCampaign";
import SubmissionNotes from "./SubmissionNotes";
import EmailTemplateDialog from "./EmailTemplateDialog";

const STATUS_LABEL: Record<FundingCampaignStatus, string> = {
  SUBMITTED: "Submitted",
  FORWARDED: "Forwarded",
  OFFER_RECEIVED: "Offer Received",
  APPLICATION_READY: "Application Ready",
};


const formatDate = (iso: string) =>
  new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });

const Field: React.FC<{ icon: string; label: string; value?: React.ReactNode }> = ({
  icon,
  label,
  value,
}) => (
  <div className="flex align-items-start gap-3 mb-4">
    <div
      className="flex align-items-center justify-content-center border-circle flex-shrink-0"
      style={{ width: "2.25rem", height: "2.25rem", background: "var(--primary-50)", border: "1px solid var(--primary-600)" }}
    >
      <i className={`pi ${icon}`} style={{ color: "var(--primary-600)", fontSize: "0.85rem" }} />
    </div>
    <div className="flex flex-column gap-1">
      <span className="text-xs font-semibold text-600 uppercase" style={{ letterSpacing: "0.07em" }}>
        {label}
      </span>
      <span className="text-lg text-800">
        {value ?? <span className="text-400">—</span>}
      </span>
    </div>
  </div>
);

const STATUS_STEPS: FundingCampaignStatus[] = [
  "SUBMITTED",
  "FORWARDED",
  "OFFER_RECEIVED",
  "APPLICATION_READY",
];

const StatusStepper: React.FC<{ current: FundingCampaignStatus }> = ({ current }) => {
  const currentIdx = STATUS_STEPS.indexOf(current);
  return (
    <div className="flex align-items-start justify-content-between mb-4">
      {STATUS_STEPS.map((step, idx) => {
        const done = idx < currentIdx;
        const active = idx === currentIdx;
        return (
          <div key={step} className="flex flex-column align-items-center flex-1" style={{ position: "relative" }}>
            {idx < STATUS_STEPS.length - 1 && (
              <div
                style={{
                  position: "absolute",
                  top: "1rem",
                  left: "50%",
                  width: "100%",
                  height: "2px",
                  background: done || active ? "var(--primary-400)" : "var(--surface-200)",
                  zIndex: 0,
                }}
              />
            )}
            <div
              className="border-circle flex align-items-center justify-content-center flex-shrink-0"
              style={{
                width: "2rem",
                height: "2rem",
                zIndex: 1,
                background: active
                  ? "var(--primary-500)"
                  : done
                  ? "var(--primary-100)"
                  : "var(--surface-200)",
                border: active ? "2px solid var(--primary-500)" : "2px solid transparent",
              }}
            >
              {done ? (
                <i className="pi pi-check text-xs" style={{ color: "var(--primary-600)" }} />
              ) : (
                <span
                  className="text-xs font-bold"
                  style={{ color: active ? "#fff" : "var(--text-color-secondary)" }}
                >
                  {idx + 1}
                </span>
              )}
            </div>
            <span
              className="text-xs mt-2 text-center"
              style={{
                color: active ? "var(--primary-600)" : done ? "var(--primary-400)" : "var(--text-color-secondary)",
                fontWeight: active ? 600 : 400,
                maxWidth: "4.5rem",
                lineHeight: 1.3,
              }}
            >
              {STATUS_LABEL[step]}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const STATUS_OPTIONS = [
  { label: "Submitted", value: "SUBMITTED" },
  { label: "Forwarded", value: "FORWARDED" },
  { label: "Offer Received", value: "OFFER_RECEIVED" },
  { label: "Application Ready", value: "APPLICATION_READY" },
];

const WallboxFundingDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const toastRef = useRef<Toast>(null);

  const [data, setData] = useState<FundingCampaignSubmissionDetail | null>(null);
  const [history, setHistory] = useState<FundingCampaignHistoryEntry[]>([]);
  const [notes, setNotes] = useState<FundingCampaignNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusDialogVisible, setStatusDialogVisible] = useState(false);
  const [newStatus, setNewStatus] = useState<FundingCampaignStatus | null>(null);
  const [electrician, setElectrician] = useState("");
  const [statusNote, setStatusNote] = useState("");
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  const [emailDialogVisible, setEmailDialogVisible] = useState(false);

  const fetchDetail = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const result = await getFundingCampaignSubmission(id);
      setData(result.data);
      setHistory(result.history);
      setNotes(result.notes);
    } catch (err: any) {
      setError(err.message ?? "Failed to load submission.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const openStatusDialog = () => {
    setNewStatus(data?.status ?? null);
    setElectrician(data?.forwarded_to_electrician ?? "");
    setStatusNote("");
    setStatusError(null);
    setStatusDialogVisible(true);
  };

  const handleStatusSave = async () => {
    if (!id || !newStatus) return;
    setStatusSaving(true);
    setStatusError(null);
    try {
      const payload: Parameters<typeof updateFundingCampaignStatus>[1] = { status: newStatus };
      if (newStatus === "FORWARDED") payload.electrician = electrician;
      if (statusNote.trim()) payload.note = statusNote.trim();
      const result = await updateFundingCampaignStatus(id, payload);
      setStatusDialogVisible(false);
      const { email_sent, email_recipients } = result.data;
      const emailMsg = email_sent
        ? `Email sent to ${email_recipients.join(", ")}.`
        : "Status updated (email delivery failed).";
      toastRef.current?.show({ severity: "success", summary: "Status updated", detail: emailMsg, life: 5000 });
      await fetchDetail();
    } catch (err: any) {
      setStatusError(err.message ?? "Failed to update status.");
    } finally {
      setStatusSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
        <ProgressSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="surface-section p-4">
        <Message severity="error" text={error} className="w-full" />
      </div>
    );
  }

  if (!data) return null;

  const activityFeed = [
    ...history.map((h) => ({ type: "history" as const, at: h.changed_at, entry: h })),
    ...notes.map((n) => ({ type: "note" as const, at: n.created_at, entry: n })),
  ].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());

  return (
    <div className="surface-ground p-4">
      <Toast ref={toastRef} />

      {/* Change Status Dialog */}
      <Dialog
        header="Change status"
        visible={statusDialogVisible}
        onHide={() => setStatusDialogVisible(false)}
        style={{ width: "28rem" }}
        footer={
          <div className="flex flex-column gap-2">
            <span className="text-xs text-500">
              <i className="pi pi-envelope mr-1" />
              Saving will send a status update email to the customer.
            </span>
            <div className="flex justify-content-end gap-2">
              <Button
                label="Cancel"
                className="p-button-text"
                onClick={() => setStatusDialogVisible(false)}
                disabled={statusSaving}
              />
              <Button
                label="Save"
                onClick={handleStatusSave}
                loading={statusSaving}
                disabled={!newStatus || (newStatus === "FORWARDED" && !electrician.trim())}
              />
            </div>
          </div>
        }
      >
        <div className="flex flex-column gap-5">
          {statusError && <Message severity="error" text={statusError} className="w-full" />}
          <div className="flex flex-column gap-1">
            <label className="text-sm font-medium">Status</label>
            <Dropdown
              value={newStatus}
              options={STATUS_OPTIONS}
              onChange={(e) => setNewStatus(e.value)}
              className="w-full"
            />
          </div>
          {newStatus === "FORWARDED" && (
            <div className="flex flex-column gap-1">
              <label className="text-sm font-medium">
                Electrician <span className="text-red-500">*</span>
              </label>
              <InputText
                value={electrician}
                onChange={(e) => setElectrician(e.target.value)}
                placeholder="e.g. Elektro Müller GmbH"
                maxLength={200}
                className="w-full"
              />
            </div>
          )}
          <div className="flex flex-column gap-1">
            <label className="text-sm font-medium">Note (optional)</label>
            <InputTextarea
              value={statusNote}
              onChange={(e) => setStatusNote(e.target.value)}
              rows={3}
              maxLength={2000}
              className="w-full"
              autoResize
            />
          </div>
        </div>
      </Dialog>

      <EmailTemplateDialog
        data={data}
        visible={emailDialogVisible}
        onHide={() => setEmailDialogVisible(false)}
        onCopied={() =>
          toastRef.current?.show({
            severity: "success",
            summary: "Kopiert",
            detail: "E-Mail-Vorlage wurde in die Zwischenablage kopiert.",
            life: 3000,
          })
        }
      />

      {/* Header */}
      <div className="flex align-items-start justify-content-between mb-4">
        <div>
          <h2 className="m-0 text-2xl font-bold">
            {data.first_name} {data.last_name}
          </h2>
          <p className="text-color-secondary text-sm m-0 mt-1">
            Submitted {formatDate(data.created_at)}
          </p>
        </div>
        <Button
          label="E-Mail-Vorlage generieren"
          icon="pi pi-envelope"
          className="p-button-outlined"
          onClick={() => setEmailDialogVisible(true)}
        />
      </div>

      <div className="grid">
        {/* Contact + Status row — equal height */}
        <div className="col-12 lg:col-8">
          <Card title="Contact information" className="h-full">
            <div className="grid">
              <div className="col-12 md:col-6">
                <Field icon="pi-user" label="Name" value={`${data.first_name} ${data.last_name}`} />
              </div>
              <div className="col-12 md:col-6">
                <Field
                  icon="pi-phone"
                  label="Phone"
                  value={data.phone ? <a href={`tel:${data.phone}`}>{data.phone}</a> : undefined}
                />
              </div>
              <div className="col-12 md:col-6">
                <Field
                  icon="pi-envelope"
                  label="Email"
                  value={<a href={`mailto:${data.email}`}>{data.email}</a>}
                />
              </div>
              <div className="col-12 md:col-6">
                <Field
                  icon="pi-users"
                  label="CC emails"
                  value={data.cc_emails && data.cc_emails.length > 0 ? data.cc_emails.join(", ") : undefined}
                />
              </div>
            </div>
          </Card>
        </div>

        <div className="col-12 lg:col-4">
          <Card title="Status" className="h-full">
            <StatusStepper current={data.status} />
            {data.forwarded_to_electrician && (
              <Field icon="pi-bolt" label="Forwarded to electrician" value={data.forwarded_to_electrician} />
            )}
            <Button
              label="Change status"
              icon="pi pi-pencil"
              className="p-button-outlined w-full"
              onClick={openStatusDialog}
            />
          </Card>
        </div>

        {/* Bottom left: Property, Notes, Questionnaire */}
        <div className="col-12 lg:col-8 flex flex-column gap-4">
          {/* Property card */}
          <Card title="Property details">
            <div className="grid">
              <div className="col-12 md:col-6">
                <Field icon="pi-building" label="Company" value={data.company} />
              </div>
              <div className="col-12 md:col-6">
                <Field icon="pi-car" label="Parking spaces" value={data.parking_spaces} />
              </div>
              <div className="col-12 md:col-6">
                <Field icon="pi-map-marker" label="Street" value={data.street} />
              </div>
              <div className="col-12 md:col-6">
                <Field icon="pi-map-marker" label="Postal code" value={data.postal_code} />
              </div>
              <div className="col-12 md:col-6">
                <Field icon="pi-map-marker" label="City" value={data.city} />
              </div>
            </div>
          </Card>

          {/* Notes */}
          <SubmissionNotes
            submissionId={id!}
            notes={notes}
            onNoteAdded={fetchDetail}
          />

          {/* Questionnaire answers */}
          {(data.answers && Object.keys(data.answers).length > 0) ||
            data.electrician_consent != null ? (
            <Card title="Questionnaire answers">
              {data.electrician_consent != null && (
                <>
                  <div className="flex flex-column gap-1 mb-2">
                    <span className="text-sm font-semibold text-800">
                      Electrician consent
                    </span>
                    <span className="text-900">
                      {data.electrician_consent === 1 ? (
                        <span className="text-green-600 font-medium">Yes</span>
                      ) : (
                        <span className="text-red-500 font-medium">No</span>
                      )}
                      {data.electrician_consent_at && (
                        <span className="text-500 text-sm ml-2">
                          · {formatDate(data.electrician_consent_at)}
                        </span>
                      )}
                    </span>
                  </div>
                  {data.answers && Object.keys(data.answers).length > 0 && (
                    <Divider className="my-2" />
                  )}
                </>
              )}
              {data.answers &&
                Object.entries(data.answers).map(([key, val], idx, arr) => (
                  <React.Fragment key={key}>
                    <div className="flex flex-column gap-1 mb-2">
                      <span className="text-sm font-semibold text-800">{key}</span>
                      <span className="text-900">{val}</span>
                    </div>
                    {idx < arr.length - 1 && <Divider className="my-2" />}
                  </React.Fragment>
                ))}
            </Card>
          ) : null}
        </div>

        {/* Bottom right: Activity */}
        <div className="col-12 lg:col-4">
          {activityFeed.length > 0 && (
            <Card title="Activity">
              <ul className="list-none p-0 m-0">
                {activityFeed.map((item, idx) => (
                  <li key={item.entry.id}>
                    <div className="flex gap-3">
                      {/* Icon + vertical line */}
                      <div className="flex flex-column align-items-center flex-shrink-0">
                        <div
                          className="border-circle flex align-items-center justify-content-center flex-shrink-0"
                          style={{
                            width: "2rem",
                            height: "2rem",
                            background:
                              item.type === "note"
                                ? "var(--blue-50)"
                                : "var(--green-50)",
                          }}
                        >
                          <i
                            className={`pi ${item.type === "note" ? "pi-comment" : "pi-arrow-right-arrow-left"} text-xs`}
                            style={{
                              color:
                                item.type === "note"
                                  ? "var(--blue-500)"
                                  : "var(--green-600)",
                            }}
                          />
                        </div>
                        {idx < activityFeed.length - 1 && (
                          <div
                            className="flex-1"
                            style={{ width: "2px", background: "var(--surface-200)", minHeight: "1.5rem", margin: "4px 0" }}
                          />
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 pb-5">
                        {item.type === "history" ? (
                          <>
                            <div className="text-sm font-semibold text-900">
                              {item.entry.from_status
                                ? `${STATUS_LABEL[item.entry.from_status]} → ${STATUS_LABEL[item.entry.to_status]}`
                                : STATUS_LABEL[item.entry.to_status]}
                            </div>
                            {item.entry.electrician && (
                              <div className="text-sm text-600 mt-1">
                                <i className="pi pi-bolt mr-1 text-xs" />
                                {item.entry.electrician}
                              </div>
                            )}
                            {item.entry.note && (
                              <div
                                className="text-sm text-700 mt-2 p-2 border-round"
                                style={{ background: "var(--surface-50)", borderLeft: "3px solid var(--surface-300)" }}
                              >
                                {item.entry.note}
                              </div>
                            )}
                          </>
                        ) : (
                          <div
                            className="text-sm text-700 p-2 border-round"
                            style={{ background: "var(--blue-50)", borderLeft: "3px solid var(--blue-200)" }}
                          >
                            {item.entry.body}
                          </div>
                        )}
                        <div className="text-xs text-400 mt-1">
                          {item.type === "history"
                            ? (item.entry as FundingCampaignHistoryEntry).changed_by
                            : (item.entry as FundingCampaignNote).created_by}
                          {" · "}
                          {formatDate(item.at)}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default WallboxFundingDetailPage;
